// Tiny fetch wrapper with consistent JSON handling and an `ApiError` for
// status-aware UI (e.g. 402 -> show subscription banner, 401 -> redirect).
//
// Offline behavior: every successful GET response is mirrored into
// IndexedDB (see lib/idb-cache.ts). When fetch throws (no network)
// or the response is a 5xx, we fall back to the most recent cached
// copy and emit a `fk:served-from-cache` event so the UI can render a
// "showing cached data" badge. Mutations bypass the cache and write
// straight through the outbox.

import { cachePut, cachedGet, invalidateForMutation } from "./idb-cache";

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

interface Options {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

// When we're loaded inside the Capacitor iOS / Android shell, the
// WebView's origin is `capacitor://localhost` (iOS) or
// `https://localhost` (Android) — a relative `/api/...` path would 404
// because the local bundle has no server. Rewrite to the real backend
// origin. The backend's CORS middleware allows these origins explicitly
// and the session cookie uses SameSite=None so it follows along.
//
// On the web, `nativeOrigin` is null and we fall through to relative
// paths exactly like before.
function nativeOrigin(): string | null {
  if (typeof window === "undefined") return null;
  const cap = window.Capacitor as
    | { isNativePlatform?: () => boolean }
    | undefined;
  if (!cap?.isNativePlatform?.()) return null;
  return "https://fieldkrew.com";
}

function resolveUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path; // already absolute
  const origin = nativeOrigin();
  if (!origin) return path;
  return path.startsWith("/") ? `${origin}${path}` : `${origin}/${path}`;
}

// Exported so auth pages (SignIn / SignUp / ForgotPassword / ResetPassword)
// can use the same path resolution as the api() wrapper without going
// through its caching + demo-toast machinery. Use this any time you
// have a raw `fetch("/api/...")` that needs to work in the Capacitor
// shell — without it, the path resolves to capacitor://localhost/api/...
// on cold start and fails with a network error.
export function resolveApiUrl(path: string): string {
  return resolveUrl(path);
}

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase();
  const isGet = method === "GET" || method === "HEAD";

  let res: Response;
  try {
    res = await fetch(resolveUrl(path), {
      method,
      headers: {
        "Content-Type": "application/json",
        ...opts.headers,
      },
      body: opts.body != null ? JSON.stringify(opts.body) : undefined,
      credentials: "include",
    });
  } catch (networkErr) {
    // Network is gone (no signal, airplane mode, server unreachable).
    // For GETs, try to serve the most recent cached copy — exactly the
    // "show me yesterday's job list while I'm in a basement" use case.
    if (isGet) {
      const cached = await cachedGet(path);
      if (cached !== null) {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("fk:served-from-cache", { detail: { path } }),
          );
        }
        return cached as T;
      }
    }
    // Network died AND we had no cached copy to fall back on. Same
    // toast-surface event as a 500 so the user sees "Failed to load"
    // instead of a blank card.
    if (isGet && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("fk:load-error", {
          detail: { path, status: 0, message: "You appear to be offline." },
        }),
      );
    }
    throw new ApiError(
      networkErr instanceof Error ? networkErr.message : "Network unavailable",
      0,
      "network_offline",
    );
  }

  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    // Some 5xx responses still have a useful cached fallback for GETs
    // (e.g., D1 transient outage). 4xx is the server saying "no" with
    // intent — never fall through to cache.
    if (isGet && res.status >= 500) {
      const cached = await cachedGet(path);
      if (cached !== null) {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("fk:served-from-cache", { detail: { path } }),
          );
        }
        return cached as T;
      }
    }
    const msg = (data as { message?: string } | null)?.message ?? `Request failed (${res.status})`;
    const code = (data as { code?: string } | null)?.code;

    // Pro-gated 402: only pop the upgrade modal on USER-INITIATED
    // ACTIONS (mutations), not on GETs. Page-load fetches would spam
    // the modal every time a Free user navigated to a Pro tab — the
    // route-level <RequirePro> guard handles that case instead, by
    // rendering a dedicated upgrade screen for the whole page.
    const isMutation = method !== "GET" && method !== "HEAD";
    if (
      res.status === 402 &&
      code === "upgrade_required" &&
      isMutation &&
      typeof window !== "undefined"
    ) {
      window.dispatchEvent(
        new CustomEvent("fk:upgrade-required", {
          detail: { message: msg, feature: (data as { feature?: string } | null)?.feature },
        }),
      );
    }
    // Demo lockdown: every write 403s with `demo_disabled`. Fire a soft toast
    // ("Sign up free to try this") so the visitor sees something useful
    // instead of an opaque error. Listened to by AppLayout's toast surface.
    if (
      res.status === 403 &&
      code === "demo_disabled" &&
      typeof window !== "undefined"
    ) {
      window.dispatchEvent(
        new CustomEvent("fk:demo-blocked", {
          detail: { message: msg },
        }),
      );
    }
    // Pro launch kill switch — the backend returns 403 + pro_sales_paused
    // for /api/billing/checkout while sales are paused. Pop the waitlist
    // modal instead of bubbling a generic error toast. Throw a marker
    // error so callers that already have try/catch don't render an
    // additional inline error string.
    if (
      res.status === 403 &&
      code === "pro_sales_paused" &&
      typeof window !== "undefined"
    ) {
      window.dispatchEvent(
        new CustomEvent("fk:open-pro-waitlist", {
          detail: { source: "checkout_blocked" },
        }),
      );
      throw new ApiError(msg, res.status, code);
    }
    // Server-side failures on a GET that had no cached fallback used to
    // silently disappear into every widget's own `.catch(() => setData(null))`,
    // leaving contractors staring at blank cards with no error and no
    // retry affordance. Fire a global toast event so the AppLayout
    // surface can render "Failed to load — retry?" once per burst of
    // failures without every page having to wire it up individually.
    // Skipped for 4xx (that's the server saying "no" with intent — the
    // caller's own inline error handling is right for those).
    if (
      isGet &&
      res.status >= 500 &&
      typeof window !== "undefined"
    ) {
      window.dispatchEvent(
        new CustomEvent("fk:load-error", {
          detail: { path, status: res.status, message: msg },
        }),
      );
    }
    throw new ApiError(msg, res.status, code);
  }

  // Success path: cache the GET response, invalidate matching cache
  // for the mutation. Both are fire-and-forget — never block the
  // request on cache-layer I/O.
  if (isGet) {
    void cachePut(path, data);
  } else {
    void invalidateForMutation(path);
  }

  return data as T;
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}
